import { Component, ElementRef, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogService } from '../common/dialog.service';
import {
  ListAction,
  ListActionMeta,
  ListConfig,
  ListQuery,
} from '../list/list.types';
import { TranslateService } from '@ngx-translate/core';
import {
  CoursesService,
  ExamsService,
  LiveEventsService,
} from '../../api/services';
import { AuthService } from '../../auth/auth.service';
import {
  BaseQueryResponse,
  Exam,
  LiveEvent,
  LiveEventQuery,
  QuestionModel,
} from '../../api/models';
import { filter, switchMap, tap } from 'rxjs/operators';
import { Panel, PanelEntity, PanelEntityType } from '../panel/panel.types';
import * as fileSaver from 'file-saver';
import { PanelService } from '../panel/panel.service';
import { CancelConfirmationComponent } from '../common/dialogs/cancel-confirmation.component';
import SampleLiveEvent from '../../../assets/import-sample-files/Sample-live-event.json';
import ChristmasLiveEvent from '../../../assets/import-sample-files/Christmas-live-event.json';
import { XLSXReader } from '../common/import-helpers/xlsxReader';
import {
  INFO_DIALOG_WIDTH,
  SentryInfo,
  settingAnswers,
  settingData,
  validateQuestions,
} from 'src/app/utility/app.utils';
import { QuickFeedbackOverlayService } from '../common/quick-feedback-overlay/quick-feedback-overlay.service';
import { ImportSnackBarComponent } from '../panel/import-snack-bar.component';
import { SampleLiveEventDialogComponent } from './sample-live-event-dialog.component';
import { UserJourneyService } from '../pages/onboarding-steps/user-journey.service';
import {
  ListInfo,
  NoEntityInfoComponent,
} from '../list/no-entity-info/no-entity-info.component';
import { EditService } from '../edit/edit.service';
import { LiveEventEditConfigService } from '../edit/configs/live-event.service';
import {
  multiSelectAction,
  GridDataComponent,
} from '../list/grid-data/grid-data.component';
import { ChipType } from 'src/app/shared/components/custom-chip/custom-chip.component';
import { SubscriptionLimit } from '../common/dialogs/subscription-limit-warning/subscription-limit-warning.component';
import { SubscriptionLimitService } from '../common/dialogs/subscription-limit-warning/subscription-limit.service';

import {
  AmplitudeSource,
  SourceType,
  UIEventEnum,
} from 'src/app/utility/amplitude-tracking/tracking.types';
import { EmbedDialogData } from '../panel/embed-dialog.component';
import { QuestionType } from 'src/app/app.types';
import {
  ConfirmConvertFromQuizComponent,
  ShowInfoItem,
} from '../common/dialogs/confirm-convert-from-quiz/confirm-convert-from-quiz.component';
import { firstValueFrom } from 'rxjs';
import { EventQuestionPanel } from '../panel/panels/event-question-panel';
import { EventsPanel } from '../panel/panels/events-panel';
import { TrackingService } from 'src/app/utility/amplitude-tracking/tracking.service';
import { AIImportDialogComponentData } from '../common/dialogs/ai-import-dialog/ai-import-dialog.component';
import { TourTrackType } from '../pages/onboarding/guided-tour';

@Component({
  selector: 'qa-cms-live-event-select-event',
  templateUrl: './cms-live-event-select-event.component.html',
  styleUrls: ['./cms-live-event-select-event.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [GridDataComponent, NoEntityInfoComponent],
})
export class CmsLiveEventSelectEventComponent {
  eventListConfig: ListConfig<any>;
  listInfo: ListInfo = {
    videoUrl: 'https://www.youtube-nocookie.com/embed/I59JLAwaUSQ',
    learnMoreText: 'liveEventList.liveEventAcademy',
    content: 'liveEventList.eventContent',
    links: [
      {
        label: 'liveEventList.eventLinks.whatUseLiveEventFor',
        url: 'https://quizacademy.de/akademie/live-event/#Einsatzm%C3%B6glichkeiten',
      },
      {
        label: 'liveEventList.eventLinks.howCreateLiveEvent',
        url: 'https://quizacademy.de/akademie/live-event/#Live-Event+erstellen',
      },
      {
        label: 'liveEventList.eventLinks.howAddLiveEvents',
        url: 'https://quizacademy.de/akademie/live-event/#Live-Event+hinzuf%C3%BCgen',
      },
      {
        label: 'liveEventList.eventLinks.howReleaseLiveEvent',
        url: 'https://quizacademy.de/akademie/live-event/#Live-Event+freigeben',
      },
      {
        label: 'liveEventList.eventLinks.furtherHelp',
        url: 'https://quizacademy.de/akademie/live-event/',
      },
    ],
  };

  constructor(
    private dialogService: DialogService,
    private matDialog: MatDialog,
    private matSnackBar: MatSnackBar,
    private liveEventsService: LiveEventsService,
    public router: Router,
    elementRef: ElementRef<HTMLElement>,
    private translator: TranslateService,
    private auth: AuthService,
    private panelService: PanelService,
    private feedbackService: QuickFeedbackOverlayService,
    private userJourney: UserJourneyService,
    private editService: EditService,
    private subscriptionLimit: SubscriptionLimitService,
    private exams: ExamsService,
    private trackingService: TrackingService,
    eventPanel: EventsPanel,
    eventQuestionsPanel: EventQuestionPanel,
    route: ActivatedRoute
  ) {
    elementRef.nativeElement.classList.add('qa-cms-live-event-select-event');
    if (!panelService?.panels.length) {
      panelService.init([eventPanel, eventQuestionsPanel], route);
    }
    this.initLiveEventListConfig();
  }

  initLiveEventListConfig() {
    const eventPanelEntity = this.panelService.panels.find(
      (panel) => panel.type === PanelEntityType.Event
    );
    const defaultQuizListConfig = getLiveEventListConfig(
      this.liveEventsService,
      this.matDialog,
      this.dialogService,
      this.matSnackBar,
      this.translator,
      this.router,
      this.auth,
      this.panelService,
      this.userJourney,
      this.editService,
      this.subscriptionLimit,
      this.feedbackService,
      this.exams,
      eventPanelEntity,
      this.trackingService
    );
    const actions = defaultQuizListConfig.actions;
    const play: ListAction = {
      icon: 'play_circle',
      name: 'select',
      isDisabled: (meta) => {
        return meta?.row ? (meta?.row as LiveEvent).status === 'END' : false;
      },
      tooltip: 'liveEvents.list.selectEvent',
      label: this.translator.instant('liveEvents.tooltip.startLiveEvent'),
      click: (meta) => {
        this.selectEvent(meta.row as LiveEvent);
      },
      type: 'button',
    };
    actions.splice(0, 0, play);
    const stats: ListAction = {
      icon: 'timeline',
      name: 'stats',
      label: this.translator.instant('content.quiz.actionMenu.openStatistics'),
      isDisabled: (meta) => {
        return (meta.row as LiveEvent).status !== 'END';
      },
      click: (meta) => {
        this.router.navigate(['/cms/live-events', (meta.row as LiveEvent).id]);
      },
      tooltip: 'content.quiz.actionMenu.openStatistics',
      type: 'button',
    };
    actions.splice(1, 0, stats);
    const overrideQuizListConfig: Partial<ListConfig<any>> = {
      actions,
      multiselectActions: [
        {
          icon: 'delete',
          name: 'question.tooltip.delete',
          type: 'button',
          click: (meta) => {
            return new Promise((resolve) => {
              this.dialogService
                .confirmDelete(
                  null,
                  this.translator.instant('common.message.deleteMulti', {
                    count: meta.rows.length,
                  })
                )
                .pipe(filter((isConfirmed) => isConfirmed))
                .subscribe(async () => {
                  await multiSelectAction(
                    meta.rows,
                    async (item: any) => {
                      await this.liveEventsService
                        .deleteLiveEvent({ id: item.id })
                        .toPromise();
                      return true;
                    },
                    `${this.translator.instant(
                      'common.label.deleting'
                    )} ${this.translator.instant('common.label.liveEvent')}`,
                    this.matDialog
                  );
                  meta.dataSource.refresh();
                  resolve();
                });
            });
          },
        },
      ],
    };
    this.eventListConfig = {
      ...defaultQuizListConfig,
      ...overrideQuizListConfig,
    };
  }

  async selectEvent(liveEvent: LiveEvent) {
    this.router.navigate(['/cms/live-events', liveEvent.id], {
      queryParams: {
        publish: true,
      },
    });
  }

  async create() {
    const event = await this.editService.open(LiveEventEditConfigService);
    this.router.navigateByUrl(`/cms/live-events/${event.id}`);
  }
}

export function getLiveEventListConfig(
  events: LiveEventsService,
  matDialog: MatDialog,
  dialog: DialogService,
  matSnackBar: MatSnackBar,
  translator: TranslateService,
  router: Router,
  auth: AuthService,
  panelService: PanelService,
  userJourney: UserJourneyService,
  editService: EditService,
  subscriptionLimit: SubscriptionLimitService,
  feedbackService?: QuickFeedbackOverlayService,
  exams?: ExamsService,
  eventPanel?: Panel<PanelEntity>,
  trackingService?: TrackingService
): ListConfig<LiveEvent> {
  function onTrackEventMarket() {
    if (!feedbackService) return;
    dialog
      .confirm({
        title: translator.instant('liveEvents.market.feedback.title'),
        text: translator.instant('liveEvents.market.feedback.text'),
        buttons: [
          {
            text: translator.instant('common.label.no'),
            color: 'warning',
            icon: 'close',
            value: 'no',
          },
          {
            text: translator.instant('common.label.yes'),
            color: 'primary',
            icon: 'done',
            value: 'yes',
          },
        ],
      })
      .subscribe((value: any) => {
        if (['yes', 'no'].includes(value)) {
          matSnackBar.open(
            translator.instant('liveEvents.market.feedback.thanks')
          );
          feedbackService.interestInMarket(
            'event',
            value === 'yes' ? true : false
          );
        }
      });
  }

  function onSampleLiveEvent() {
    matDialog
      .open(SampleLiveEventDialogComponent, { width: '300px' })
      .afterClosed()
      .subscribe((response: any) => {
        if (response?.data === 'Default') {
          importFromJson(SampleLiveEvent[translator.currentLang] as LiveEvent);
        }
        if (response?.data === 'Christmas') {
          importFromJson(ChristmasLiveEvent as LiveEvent);
        }
      });
  }

  async function onExportJson(
    meta: ListActionMeta<LiveEvent>,
    trackingService: TrackingService
  ) {
    const questions = await events
      .getQuestionsForEvent({
        id: meta.row.id,
        embed_answers: true,
        embed_tags: true,
      })
      .toPromise();
    const fullItem = { name: meta.row.name, questions };
    const file = `${JSON.stringify(fullItem)}\n`;
    const blob = new Blob([file], { type: 'application/json' });
    const fileName = fullItem.name.split(' ').join('-').toLowerCase();
    fileSaver.saveAs(blob, `qa-live-event-${fileName}.json`);
    trackingService.trackEvent({
      event_type: UIEventEnum.JSON_EXPORTED,
      event_properties: {
        source: AmplitudeSource.LIVE_EVENT,
      },
    });
  }

  async function importFromKahoot() {
    const excelFile = await panelService.selectExcelFileAsBinary();
    const file = XLSXReader.excelToArray(excelFile);
    const name = file[0][0];
    if (!name || !name.trim()) {
      matSnackBar.open(
        translator.instant(
          'liveEvents.list.importFromExcel.provideNameForLiveEvent'
        )
      );
      return;
    }
    let liveEvent;
    const snackbar = matSnackBar.open(
      translator.instant('liveEvents.list.importFromExcel.createLiveEvent')
    );
    const questions = panelService.importQuestionsFromKahoot(excelFile);
    let questionArr = settingData(questions);
    settingAnswers(questionArr);
    questionArr.forEach((question) => {
      if (question['type'] === 'True/False') {
        question['type'] = 1;
        if (question['answers'].length && question['answers'][0]['is_right']) {
          question['is_right'] = question['answers'][0]['is_right'];
        }
        delete question['answers'];
      } else if (question['type'] === 'Multiple Choice') {
        question['type'] = 0;
      } else if (question['type'] === 'Single Choice') {
        question['type'] = 2;
      }
    });
    questionArr = questionArr
      .filter((q) => q.type === QuestionType.TRUE_FALSE || q.answers?.length)
      .map((q) => ({
        ...q,
        is_poll_question:
          q.type === QuestionType.FREETEXT ? true : q.is_poll_question,
      }));
    const sentryInfo: SentryInfo = {
      title: `Error while importing live event from kahoot`,
      api: 'copyQuestionToEvent',
      preventLogs: true,
      file: 'cms-lve-event-select-exam.component.ts -> importFromKahoot()',
    };
    const { isValid } = validateQuestions(
      sentryInfo,
      questionArr,
      translator,
      matSnackBar
    );
    if (!isValid) {
      return;
    }
    if (name) {
      liveEvent = await events
        .createLiveEvent({
          body: {
            name: name,
          } as LiveEvent,
          source: SourceType.KAHOOT,
        })
        .toPromise();
      matSnackBar.open(
        translator.instant(
          'liveEvents.list.importFromExcel.addQuestionsToLiveEvent'
        )
      );
      userJourney.set('importedLiveEvent');
      await events
        .copyQuestionToEvent({
          id: liveEvent.id,
          body: questionArr,
        })
        .toPromise();
      snackbar.dismiss();
      matSnackBar.open(
        translator.instant('liveEvents.list.importFromExcel.liveEventCreated')
      );
      router.navigate(['cms', 'live-events', liveEvent.id]);
    }
  }

  async function importFromJson(item?: LiveEvent) {
    const jsonFile =
      item || ((await panelService.promptFile('json')) as LiveEvent);
    if (
      jsonFile?.questions &&
      jsonFile?.questions?.length &&
      jsonFile.questions.length > 50
    ) {
      matDialog.open(CancelConfirmationComponent, {
        autoFocus: false,
        data: {
          messages: [
            translator.instant(
              'liveEvents.list.importErrorMessage.maximumQuestions'
            ),
            translator.instant(
              'liveEvents.list.importErrorMessage.addMoreQuestions',
              {
                mailto: 'kontakt@quizacademy.de',
              }
            ),
          ],
          buttonLabels: {
            confirm: translator.instant('common.button.ok'),
          },
        },
        panelClass: 'live-events-import-dialog',
      });
      return;
    }
    jsonFile?.questions?.forEach((question) => {
      delete question.id;
      if (question?.type === QuestionType.FREETEXT) {
        question.is_poll_question = true;
      }
    });
    if (jsonFile?.name) {
      matSnackBar.open(translator.instant('liveEvents.list.importingJson'));
      const sentryInfo = {
        title: `Error while importing live event from JSON`,
        file: 'cms-lve-event-select-exam.component.ts -> importFromJson()',
        api: 'copyQuestionToExam',
      };
      const { isValid } = validateQuestions(
        sentryInfo,
        jsonFile.questions,
        translator,
        matSnackBar
      );
      if (!isValid) {
        return;
      }
      let questionsChunk = [];
      if (jsonFile.questions && jsonFile.questions.length) {
        if (jsonFile.questions.length > 10) {
          while (jsonFile.questions.length > 0) {
            questionsChunk.push(jsonFile.questions.splice(0, 10));
          }
        } else {
          questionsChunk = [jsonFile.questions];
        }
      }
      const importProgress = {
        current: 0,
        total: questionsChunk.length + 1,
      };
      const snackBarRef = matSnackBar.openFromComponent(
        ImportSnackBarComponent,
        {
          data: {
            progress: importProgress,
            message: 'content.exam.questionImport',
          },
          duration: null,
        }
      );
      const body = {
        name: jsonFile.name,
        selected_music: jsonFile.selected_music ?? undefined,
        manual_result_switch: jsonFile.manual_result_switch ?? false,
        show_top_players: true,
        show_award_ceremony: true,
        automatic_username_generation:
          jsonFile.automatic_username_generation ?? false,
      } as LiveEvent;
      const liveEvent = await events
        .createLiveEvent({
          body,
          source: item ? SourceType.MUSTER : SourceType.JSON_IMPORT,
        })
        .toPromise();
      userJourney.set('importedLiveEvent');
      importProgress.current += 1;
      if (questionsChunk.length) {
        await copyQuestionsToEvent(questionsChunk, liveEvent, importProgress);
      }
      snackBarRef.dismiss();
      matSnackBar.open(translator.instant('liveEvents.list.importSuccess'));
      matDialog.closeAll();
      router.navigate(['cms', 'live-events', liveEvent.id]);
    }
  }

  async function copyQuestionsToEvent(
    questions: QuestionModel[][],
    liveEvent: LiveEvent,
    importProgress
  ) {
    for (const items of questions) {
      await events
        .copyQuestionToEvent({ id: liveEvent.id, body: items })
        .toPromise();
      importProgress.current += 1;
    }
  }

  async function duplicateLiveEvent(meta: ListActionMeta<LiveEvent>) {
    try {
      matSnackBar.open(translator.instant('content.event.copyLiveEvent'));
      const liveEvent = meta.row;
      delete liveEvent.status;
      const liveEventCopy = await firstValueFrom(
        events.createLiveEvent({
          body: {
            name: liveEvent.name,
          } as LiveEvent,
        })
      );
      userJourney.set('createdLiveEvent');
      matSnackBar.open(translator.instant('content.event.linkQuestions'));
      const childData = await firstValueFrom(
        events.getQuestionsForEvent({
          embed_answers: false,
          id: liveEvent.id,
        })
      );
      if (childData && childData?.length) {
        let questionsChunk = [];
        if (childData.length > 10) {
          while (childData.length > 0) {
            questionsChunk.push(childData.splice(0, 10));
          }
        } else {
          questionsChunk = [childData];
        }
        const importProgress = {
          current: 0,
          total: questionsChunk.length + 1,
        };
        const snackBarRef = matSnackBar.openFromComponent(
          ImportSnackBarComponent,
          {
            data: {
              progress: importProgress,
              message: 'content.event.questionImport',
            },
          }
        );
        await copyQuestionsToEvent(
          questionsChunk,
          liveEventCopy,
          importProgress
        );
        snackBarRef.dismiss();
        matSnackBar.open(translator.instant('content.event.liveEventCopied'));
        meta.dataSource.refresh();
      } else {
        matSnackBar.open(translator.instant('content.event.liveEventCopied'));
        meta.dataSource.refresh();
      }
    } catch (error) {
      matSnackBar.open(error);
    }
  }

  function wizardImports() {
    return {
      createWithAI: {
        text: translator.instant('content.wizardActions.title.createWithAI'),
        icon: 'auto_awesome',
        description: translator.instant(
          'content.wizardActions.description.createLiveEventWithAI'
        ),
        tag: translator.instant('content.wizardActions.tag.createWithAI'),
        click: async () => {
          const event = await editService.open(LiveEventEditConfigService);
          if (event && event?.id) {
            router.navigateByUrl(`/cms/live-events/${event.id}`).then(() => {
              matDialog?.closeAll();
              const dialogData: AIImportDialogComponentData = {
                name: null,
                type: null,
                onImport: () => {
                  const queryParams = {
                    type: PanelEntityType.EventQuestion,
                  };
                  panelService.redirectToImportItems(
                    `/cms/csv-imports/question/${event.id}`,
                    queryParams,
                    true
                  );
                  matDialog?.closeAll();
                },
              };
              panelService.openChatGprDialog(dialogData);
            });
          }
        },
      },
      createButton: {
        text: translator.instant('content.wizardActions.title.createManually'),
        icon: 'edit',
        description: translator.instant(
          'content.wizardActions.description.createManually'
        ),
        click: async () => {
          const event = await editService.open(LiveEventEditConfigService);
          if (event && event?.id) {
            router.navigateByUrl(`/cms/live-events/${event.id}`);
            matDialog?.closeAll();
          }
        },
      },
      importButtons: [
        {
          text: translator.instant(
            'content.wizardActions.title.useSampleLiveEvent'
          ),
          description: translator.instant(
            'content.wizardActions.description.useSampleLiveEvent'
          ),
          icon: 'file_copy',
          click: () =>
            importFromJson(
              SampleLiveEvent[translator.currentLang] as LiveEvent
            ),
        },
        {
          text: translator.instant('content.wizardActions.title.useTemplates'),
          description: translator.instant(
            'content.wizardActions.description.useLiveEventTemplates'
          ),
          icon: 'shopping_cart',
          click: () => importFromJson(ChristmasLiveEvent as LiveEvent),
        },
        {
          text: translator.instant(
            'content.wizardActions.title.importFromJSON'
          ),
          description: translator.instant(
            'content.wizardActions.description.importFromJSON'
          ),
          icon: 'save_alt',
          click: () => {
            importFromJson();
          },
        },
        {
          text: translator.instant('content.wizardActions.title.importFromCSV'),
          description: translator.instant(
            'content.wizardActions.description.importFromCSV'
          ),
          icon: 'save_alt',
          click: () => {
            panelService.importCSV(
              LiveEventEditConfigService,
              PanelEntityType.EventQuestion
            );
          },
        },
        {
          text: translator.instant(
            'content.wizardActions.title.importFromKahoot'
          ),
          description: translator.instant(
            'content.wizardActions.description.importFromKahoot'
          ),
          icon: 'save_alt',
          click: () => {
            importFromKahoot();
          },
          extra: {
            name: translator.instant(
              'liveEvents.liveEventDialogTitles.howItWorks'
            ),
            type: 'link',
            icon: 'keyboard_arrow_right',
            click: () => {
              window.open(
                'https://quizacademy.de/tipps/kahoot-inhalte-importieren/',
                '_blank'
              );
            },
          },
        },
        {
          text: translator.instant(
            'content.wizardActions.title.convertFromQuiz'
          ),
          description: translator.instant(
            'content.wizardActions.description.liveEventFromQuiz'
          ),
          icon: 'transform',
          click: () => {
            matDialog.closeAll();
            router.navigate(['/cms/import-items'], {
              queryParams: {
                toLiveEvent: true,
                type: PanelEntityType.Quiz,
              },
            });
          },
        },
        {
          text: translator.instant(
            'content.wizardActions.title.convertFromExam'
          ),
          description: translator.instant(
            'content.wizardActions.description.convertFromExam'
          ),
          icon: 'transform',
          disabled: true,
        },
      ],
    };
  }

  const ret: ListConfig<LiveEvent> = {
    hideWrapper: false,
    tutorial: 'liveEvent',
    showGuidedTour: true,
    extraTutorial: 'https://quizacademy.de/akademie/live-event/',
    tutorialLabel: 'dashboardNew.liveEventCard.liveEventVideo',
    extraTutorialLabel: 'dashboardNew.liveEventCard.academicButton',
    progressStepType: TourTrackType.LIVE_EVENT,
    chip: {
      headerText: 'common.label.status',
      name: 'status',
      hide: (meta) => {
        return (
          meta?.row?.status === 'WAITING_START' ||
          meta?.row?.status === 'PRE_START'
        );
      },
      label: (meta) => {
        return meta.row?.status === 'END'
          ? 'exam.setup.label.completed'
          : 'exam.setup.label.inProgress';
      },
      type: (meta) =>
        meta.row?.status === 'END' ? ChipType.PRIMARY_LIGHT : ChipType.DEFAULT,
      icon: (meta) => {
        return meta.row?.status === 'END' ? 'check' : 'timer';
      },
    },
    actions: [
      {
        click: (meta) => {
          router.navigate(['/cms/live-events', meta.row.id]);
        },
        icon: 'edit',
        name: 'edit',
        tooltip: 'common.tooltip.edit',
        type: 'button',
        hide: () => true,
      },
    ],
    actionsMenu: [
      {
        click: () => onTrackEventMarket(),
        name: 'share',
        icon: 'share',
        label: 'liveEvents.market.feedback.share',
        type: 'button',
      },
      {
        icon: 'content_copy',
        name: 'duplicate',
        label: translator.instant('content.quiz.actionMenu.duplicateLiveEvent'),
        type: 'button',
        click: (meta) => duplicateLiveEvent(meta),
      },
      {
        icon: 'transform',
        name: 'convert',
        label: translator.instant('content.quiz.actionMenu.convertToExam'),
        type: 'button',
        click: ({ row }) =>
          convertToExamAction(
            row,
            events,
            exams,
            matSnackBar,
            translator,
            router,
            matDialog,
            null,
            null
          ),
      },
      {
        click: (meta) => onExportJson(meta, trackingService),
        icon: 'save_alt',
        name: 'download',
        label: 'content.course.actionMenu.downloadJSON',
        type: 'button',
      },
      {
        icon: 'description',
        type: 'button',
        name: 'export_csv',
        label: translator.instant('content.exam.actionMenu.downloadCSV'),
        click: (meta) => {
          panelService.exportItemAsCSV(eventPanel, meta.row.id);
        },
      },
      {
        icon: 'code',
        name: 'Embed',
        click: (row) => {
          const data: EmbedDialogData = {
            pinCode: row.row.pin_code,
            entity: PanelEntityType.Event,
          };
          dialog.openEmbedModel(data);
        },
        label: 'content.course.actionMenu.embed',
        type: 'button',
      },
      {
        click: ({ row, dataSource }) => {
          dialog
            .confirmDelete()
            .pipe(
              filter((isConfirmed) => isConfirmed),
              switchMap(() => events.deleteLiveEvent({ id: row.id }))
            )
            .subscribe({
              next: () => {
                matSnackBar.open(
                  translator.instant('content.quiz.deleteItem.success')
                );
                dataSource.refresh();
              },
              error: () => {
                matSnackBar.open(
                  translator.instant('content.quiz.deleteItem.error')
                );
              },
            });
        },
        icon: 'delete',
        name: 'delete',
        label: 'quiz.tooltip.delete',
        type: 'button',
      },
    ],
    activeActionNames: ['edit', 'share', 'select', 'chip', 'stats'],
    activeColumnNames: ['id', 'name'],
    columns: [
      {
        label: 'ID',
        name: 'id',
        type: 'number',
        sortable: true,
        width: '64px',
        filter: (value) => {
          return {
            id: Number(value),
          };
        },
      },
      {
        click: (meta) => {
          router.navigate(['/cms/live-events', meta.row.id]);
        },
        icon: (meta) => {
          if (meta.row.created_by !== auth.currentUserInfo$.value.sub) {
            return 'shopping_cart';
          } else {
            return null;
          }
        },
        label: 'quiz.table.name',
        name: 'name',
        sortable: true,
        primary: true,
        filter: (value) => {
          return {
            name: value,
          };
        },
      },
    ],
    wizardImports: wizardImports(),
    imports: [
      {
        title: translator.instant('liveEvents.list.sampleLiveEvent'),
        description: translator.instant('liveEvents.list.sampleLiveEventDesc'),
        action: {
          text: translator.instant(
            'liveEvents.liveEventDialogTitles.importMusterLiveEvent'
          ),
          icon: 'save_alt',
          click: () => onSampleLiveEvent(),
        },
      },
      {
        title: translator.instant('liveEvents.list.importFromJson'),
        description: translator.instant(
          'liveEvents.description.importFromJSON'
        ),
        action: {
          text: translator.instant(
            'liveEvents.liveEventDialogTitles.importFromJSON'
          ),
          icon: 'save_alt',
          click: () => {
            importFromJson();
          },
        },
      },
      {
        title: translator.instant('liveEvents.list.importfromKahoot'),
        description: translator.instant(
          'liveEvents.description.importFromKahoot'
        ),
        action: {
          text: translator.instant(
            'liveEvents.liveEventDialogTitles.importFromKahoot'
          ),
          icon: 'description',
          click: () => {
            importFromKahoot();
          },
        },
        extra: {
          name: translator.instant(
            'liveEvents.liveEventDialogTitles.howItWorks'
          ),
          type: 'link',
          icon: 'keyboard_arrow_right',
          click: () => {
            window.open(
              'https://quizacademy.de/tipps/kahoot-inhalte-importieren/',
              '_blank'
            );
          },
        },
      },
      {
        title: translator.instant('liveEvents.list.importExistingQuiz'),
        description: translator.instant(
          'liveEvents.description.importExistingQuiz'
        ),
        action: {
          icon: 'move_to_inbox',
          click: () => {
            matDialog.closeAll();
            router.navigate(['/cms/import-items'], {
              queryParams: {
                toLiveEvent: true,
                type: PanelEntityType.Quiz,
              },
            });
          },
          text: translator.instant(
            'liveEvents.liveEventDialogTitles.importExistingQuiz'
          ),
        },
      },
    ],
    createButton: {
      options: [
        {
          text: translator.instant('liveEvents.list.create'),
          icon: 'add',
          click: async () => {
            const event = await editService.open(LiveEventEditConfigService);
            if (event && event?.id) {
              router.navigateByUrl(`/cms/live-events/${event.id}`);
            }
          },
        },
      ],
    },
    showSubscriptionIndicator: true,
    subscriptionLimit: SubscriptionLimit.LIVE_EVENT_COUNT,
    fetch: (queryParams?: ListQuery<LiveEvent>) => {
      return events
        .queryEvents({
          body: queryParams as unknown as LiveEventQuery,
        })
        .pipe(
          tap((response: BaseQueryResponse) => {
            subscriptionLimit.subscriptionLimit.LIVE_EVENT_COUNT =
              response.pagination.total;
          })
        );
    },
    query: {
      initial: {
        sorting: [
          {
            option: 'id',
            direction: 'DESC',
          },
        ],
      },
      searchColumnName: 'name',
    },
    name: 'liveEventsList',
    title: translator.instant('common.label.liveEvents'),
  };

  return ret;
}

export function convertToExamAction(
  event: LiveEvent,
  events: LiveEventsService,
  exams: ExamsService,
  matSnackBar,
  translator,
  router: Router,
  matDialog: MatDialog,
  courses: CoursesService,
  courseId: number
) {
  const dialogRef = matDialog.open<
    ConfirmConvertFromQuizComponent,
    ShowInfoItem
  >(ConfirmConvertFromQuizComponent, {
    data: {
      modelTitle: translator.instant(
        'course.confirmAction.convertEventToExamHint'
      ),
      confirmButtonText: translator.instant(
        'course.confirmAction.convertEventToExam'
      ),
    },
    width: INFO_DIALOG_WIDTH,
    height: 'auto',
    autoFocus: false,
    panelClass: 'confirm-convert-quiz-modal',
  });
  dialogRef.afterClosed().subscribe((resp) => {
    if (resp) {
      convertToExam(
        event,
        events,
        exams,
        matSnackBar,
        translator,
        router,
        courses,
        courseId
      );
    }
  });
}

export async function convertToExam(
  event: LiveEvent,
  events: LiveEventsService,
  exams: ExamsService,
  matSnackBar,
  translator,
  router: Router,
  courses: CoursesService,
  courseId: number
) {
  try {
    matSnackBar.open(translator.instant('content.event.convertingExam'));
    const exam = {
      name: event.name,
      scoring_schema: 'DEFAULT',
    };

    const req$ = courseId
      ? courses.createExamInCourse({ id: courseId, body: exam as Exam })
      : exams.createExam({ body: exam as Exam });

    const createdExam = await firstValueFrom(req$);

    matSnackBar.open(translator.instant('content.event.linkingQuestion'));
    const questions = await firstValueFrom(
      events.getQuestionsForEvent({ embed_answers: false, id: event.id })
    );
    const questionIds = questions.map((q) => ({ id: q.id } as QuestionModel));
    await firstValueFrom(
      exams.linkQuestionToExam({ id: createdExam.id, body: questionIds })
    );

    matSnackBar.open(translator.instant('content.event.convertedExam'));

    const queryParams = courseId ? { courseId } : null;
    router.navigate(['/cms/exams', createdExam.id], { queryParams });
  } catch (error) {
    matSnackBar.open(translator.instant('content.event.convertExamError'));
  }
}
